<template>
  <div class="mf" :class="{ 'mf--editing': editing, 'mf--card': !def.ownCard, 'mf--selected': selected }" :style="cardStyle">
    <!-- Édition : tout le module se déplace, un clic ouvre ses options ; outils au survol -->
    <template v-if="editing">
      <div class="mf-drag mf-overlay" :title="$t('dash.overview.dashboards.drag')" @pointerdown="onOverlayDown"></div>
      <span
        v-for="edge in edges"
        :key="edge"
        class="mf-rz"
        :class="`mf-rz--${edge}`"
        @pointerdown.stop.prevent="$event.button === 0 && $emit('resize-start', { edge, event: $event })"
      ></span>
      <div class="ov-mf-toolbar">
        <button v-if="def.fit" class="ov-mf-tool" :title="$t('dash.overview.dashboards.fit_height')" @click.stop="$emit('fit')">
          <i class="material-icons">unfold_less</i>
        </button>
        <button class="ov-mf-tool" :title="$t('dash.overview.dashboards.settings_widget')" @click.stop="$emit('settings')">
          <i class="material-icons">tune</i>
        </button>
        <button class="ov-mf-tool ov-mf-tool--danger" :title="$t('dash.overview.dashboards.remove_widget')" @click.stop="$emit('remove')">
          <i class="material-icons">close</i>
        </button>
      </div>
    </template>

    <div v-if="!def.ownCard && !hideHead" class="mf-head">
      <i v-if="headIcon" class="material-icons ov-title-icon" :style="titleColor ? { color: titleColor } : null">{{ headIcon }}</i>
      <Tooltip :text="help" :active="!!help && !editing" position="top" class="mf-title-wrap">
        <h2 class="mf-title" :style="titleColor ? { color: titleColor } : null">{{ title }}<i v-if="help" class="material-icons ov-help-icon">info_outline</i></h2>
      </Tooltip>
      <span v-if="subtitle" class="ov-chip-muted mf-subtitle">{{ subtitle }}</span>
      <!-- Pages concernées (nom + réseau) -->
      <span v-if="sources.length" class="mf-sources">
        <span v-for="src in shownSources" :key="src.id" class="mf-src">
          <span class="mf-src-net" :class="`mf-src-net--${src.network}`">{{ $t(`dash.overview.network.${src.network}`) }}</span>
          <span class="mf-src-name">{{ src.name }}</span>
        </span>
        <Tooltip v-if="hiddenSources.length" :text="hiddenSourcesText" position="top">
          <span class="mf-src mf-src--more">+{{ hiddenSources.length }}</span>
        </Tooltip>
      </span>
    </div>

    <!-- Blocs avec leur propre carte : focus sur certaines pages signalé au-dessus -->
    <div v-if="def.ownCard && sources.length" class="mf-focus">
      <i class="material-icons">filter_center_focus</i>
      <span class="mf-sources">
        <span v-for="src in shownSources" :key="src.id" class="mf-src">
          <span class="mf-src-net" :class="`mf-src-net--${src.network}`">{{ $t(`dash.overview.network.${src.network}`) }}</span>
          <span class="mf-src-name">{{ src.name }}</span>
        </span>
        <Tooltip v-if="hiddenSources.length" :text="hiddenSourcesText" position="top">
          <span class="mf-src mf-src--more">+{{ hiddenSources.length }}</span>
        </Tooltip>
      </span>
    </div>

    <div class="mf-body" :class="{ 'mf-body--locked': locked }">
      <div v-if="locked" class="mf-locked">
        <i class="material-icons">lock</i>
        <p>{{ $t('dash.overview.widgets.locked') }}</p>
      </div>
      <slot v-else />
    </div>
  </div>
</template>

<script>
import Tooltip from '../../tooltip/tooltipInit.vue'

const MAX_SOURCES = 2
// Poignées de redimensionnement : bords (n, s, e, w) et coins
const EDGES = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw']

/**
 * Cadre d'un widget dans la grille : carte + titre pour les nouveaux widgets,
 * calque de déplacement et outils discrets en édition
 */
export default {
  name: 'ModuleFrame',

  components: { Tooltip },

  props: {
    def: { type: Object, required: true },
    title: { type: String, default: '' },
    subtitle: { type: String, default: '' },
    help: { type: String, default: '' },
    // Pages concernées [{ id, name, network }] : pages du panneau ou focus du widget ; vide = pages du tableau de bord
    sources: { type: Array, default: () => [] },
    editing: { type: Boolean, default: false },
    // Titre masqué (option du widget) : la carte ne montre que son contenu
    hideHead: { type: Boolean, default: false },
    // Couleurs choisies dans les options (widget Texte) : fond de toute la carte, couleur du titre
    background: { type: String, default: null },
    titleColor: { type: String, default: null },
    // Icône du titre : undefined = celle du widget, null = aucune
    icon: { type: String, default: undefined },
    selected: { type: Boolean, default: false },
    locked: { type: Boolean, default: false }
  },

  data() {
    return { edges: EDGES }
  },

  computed: {
    headIcon() {
      return this.icon === undefined ? this.def.icon : this.icon
    },

    cardStyle() {
      return this.background && !this.def.ownCard ? { background: this.background, borderColor: 'transparent' } : null
    },

    shownSources() {
      return this.sources.slice(0, MAX_SOURCES)
    },

    hiddenSources() {
      return this.sources.slice(MAX_SOURCES)
    },

    hiddenSourcesText() {
      return this.hiddenSources.map(src => `${src.name} (${this.$t(`dash.overview.network.${src.network}`)})`).join(', ')
    }
  },

  methods: {
    // Glisser pour déplacer, simple clic pour ouvrir les options (géré par le tableau de bord)
    onOverlayDown(event) {
      if (event.button === 0) this.$emit('drag-start', event)
    }
  }
}
</script>

<style scoped>
.mf {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  border-radius: 14px;
}
.mf--card {
  padding: 16px 18px;
  background: #fff;
  border: 1px solid #f1edf5;
}
.mf-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  min-width: 0;
}
.mf-title-wrap {
  min-width: 0;
}
.mf-title {
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 0;
  overflow: hidden;
  font-size: 15px;
  font-weight: 600;
  color: #121826;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.mf-subtitle {
  flex: 0 0 auto;
}
.mf-head {
  flex-wrap: wrap;
}
.mf-focus {
  display: flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  margin: 0 0 6px 4px;
  color: #6b1f4d;
}
.mf-focus .material-icons {
  font-size: 14px;
}
.mf-sources {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  min-width: 0;
}
.mf-head .mf-sources {
  margin-left: auto;
}
.mf-src {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  max-width: 220px;
  padding: 2px 8px 2px 2px;
  border-radius: 10px;
  background: #f8f5fa;
  font-size: 11.5px;
  font-weight: 600;
  color: #121826;
}
.mf-src--more {
  padding: 2px 8px;
  color: #6b1f4d;
}
.mf-src-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.mf-src-net {
  flex: 0 0 auto;
  padding: 1px 6px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 700;
  background: #f1edf5;
  color: #6b1f4d;
}
.mf-src-net--facebook { background: #e7f0fd; color: #1877f2; }
.mf-src-net--instagram { background: #fde8f1; color: #c13584; }
.mf-src-net--linkedin { background: #e6f1f8; color: #0a66c2; }
.mf-src-net--tiktok,
.mf-src-net--twitter { background: #eef0f3; color: #121826; }
.mf-src-net--youtube { background: #fdeaea; color: #e62117; }
.mf-src-net--google { background: #e8f0fe; color: #1a73e8; }
.mf-src-net--pinterest { background: #fdeaea; color: #e60023; }
.mf-body {
  position: relative;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}
/* Les blocs de l'overview remplissent la case de la grille, avec défilement interne */
.mf:not(.mf--card) > .mf-body > * {
  height: 100%;
  overflow-y: auto;
}
.mf:not(.mf--card) > .mf-body > * >>> .ov-card {
  max-height: none;
}
.mf:not(.mf--card) > .mf-body > .ov-card {
  box-sizing: border-box;
}
.mf--editing .mf-body {
  pointer-events: none;
}
/* Mesure de la hauteur naturelle (ajustement au contenu), appliquée le temps d'une lecture */
.mf--measure {
  height: auto !important;
}
.mf--measure > .mf-body {
  flex: none !important;
  overflow: visible !important;
}
.mf--measure > .mf-body > * {
  height: auto !important;
}

/* Calque d'édition : contour au survol, plein à la sélection */
.mf-overlay {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 4;
  border-radius: 14px;
  cursor: move;
  transition: box-shadow 0.15s, background 0.15s;
}
.mf--editing:hover .mf-overlay {
  box-shadow: inset 0 0 0 1.5px #d3b6c6;
  background: rgba(246, 241, 250, 0.25);
}
.mf--selected .mf-overlay,
.mf--selected:hover .mf-overlay {
  box-shadow: inset 0 0 0 2px #b64985;
  background: rgba(246, 241, 250, 0.2);
}
.mf-overlay {
  touch-action: none;
}
/* Poignées de redimensionnement (invisibles, trait prune au survol) */
.mf-rz {
  position: absolute;
  z-index: 6;
  touch-action: none;
}
.mf-rz--n, .mf-rz--s { left: 14px; right: 14px; height: 10px; cursor: ns-resize; }
.mf-rz--e, .mf-rz--w { top: 14px; bottom: 14px; width: 10px; cursor: ew-resize; }
.mf-rz--n { top: -5px; }
.mf-rz--s { bottom: -5px; }
.mf-rz--e { right: -5px; }
.mf-rz--w { left: -5px; }
.mf-rz--ne, .mf-rz--nw, .mf-rz--se, .mf-rz--sw { width: 16px; height: 16px; }
.mf-rz--ne { top: -6px; right: -6px; cursor: nesw-resize; }
.mf-rz--sw { bottom: -6px; left: -6px; cursor: nesw-resize; }
.mf-rz--nw { top: -6px; left: -6px; cursor: nwse-resize; }
.mf-rz--se { bottom: -6px; right: -6px; cursor: nwse-resize; }
.mf-rz::after {
  content: '';
  position: absolute;
  border-radius: 3px;
  background: #b64985;
  opacity: 0;
  transition: opacity 0.15s;
}
.mf-rz--n::after, .mf-rz--s::after { left: 50%; top: 3px; width: 36px; height: 4px; margin-left: -18px; }
.mf-rz--e::after, .mf-rz--w::after { top: 50%; left: 3px; width: 4px; height: 36px; margin-top: -18px; }
.mf-rz--ne::after, .mf-rz--nw::after, .mf-rz--se::after, .mf-rz--sw::after { top: 4px; left: 4px; width: 8px; height: 8px; border-radius: 50%; }
.mf--editing:hover > .mf-rz::after,
.mf--selected > .mf-rz::after {
  opacity: 0.35;
}
.mf-rz:hover::after {
  opacity: 1 !important;
}
.ov-mf-toolbar {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 7;
  display: flex;
  gap: 2px;
  padding: 2px;
  border-radius: 9px;
  background: #fff;
  box-shadow: 0 2px 10px rgba(18, 24, 38, 0.12);
  opacity: 0;
  transform: translateY(-2px);
  transition: opacity 0.15s, transform 0.15s;
}
.mf--editing:hover .ov-mf-toolbar,
.mf--selected .ov-mf-toolbar {
  opacity: 1;
  transform: none;
}
.section-overview.ov.ov button.ov-mf-tool {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px !important;
  margin: 0;
  padding: 0;
  border: none;
  border-radius: 7px;
  background: transparent;
  color: #555e75;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.section-overview.ov.ov button.ov-mf-tool:hover {
  background: #f1edf5;
  color: #6b1f4d;
}
.section-overview.ov.ov button.ov-mf-tool--danger:hover {
  background: #fdeaea;
  color: #d93025;
}
.ov-mf-tool .material-icons {
  font-size: 16px;
}
.mf-locked {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 100%;
  min-height: 80px;
  color: #9ba3b7;
  font-size: 12.5px;
  text-align: center;
}
</style>
